import { LinkEvent, LinkEventType, LinkResolveEvent } from '../../../typings/event/global';
import { TSortedLinkMap } from './typings';
import LinkApi from '../../api/link';
import EventBus from '../../../util/event-bus';
import { URL_PATTERN } from '../../util/reg';
import { DBManager } from '../../storage';
import { logger } from '../../../util/logger';
import cloneDeep from 'lodash/cloneDeep';
import { DB } from '../../../typings/model/db';
import humps from 'humps';

/**
 * 链接解析管理中心
 */
class LinkManager {
  private static instance: LinkManager;
  public static getInstance(): LinkManager {
    if (!LinkManager.instance) {
      LinkManager.instance = new LinkManager();
    }

    return LinkManager.instance;
  }

  /** 最大缓存时间: 1周，超过1周的缓存，先返回缓存，同时重新再请求一次 */
  private maxCacheTime = 7 * 24 * 60 * 60 * 1000;
  /** 链接解析Map(key: link, value: ILinkMsgInfo) */
  private linkMsgMap = new Map<string, DB.ILinkMsgInfo>();
  /** 链接解析的eventBus */
  private linkEventBus = new EventBus<LinkEvent>();
  /** 链接解析的任务Map(key: linkMsgContent, value: linkList) */
  private linkMsgResolveTaskMap = new Map<string, string[]>();
  /** 无法解析的链接集合 */
  private unResolveLinkSet = new Set<string>();
  /** 是否从IndexDB查询过linkMsg的标志位记录map */
  private hasQueryLinkMsgFromIndexDBMap = new Map<string, boolean>();

  /** 添加链接信息状态变化的监听器 */
  public addLinkResolveEventListener(event: (e: LinkResolveEvent) => void | Promise<void>): void {
    this.linkEventBus.on(LinkEventType.resolve, event);
  }

  /** 移除链接信息状态变化的监听器 */
  public removeLinkResolveEventListener(event: (e: LinkResolveEvent) => void | Promise<void>): void {
    this.linkEventBus.off(LinkEventType.resolve, event);
  }

  /**
	 * 获取文本中的链接
	 * @param text 文本
	 * @param needDeWeight 是否需要去重（默认：需要）
	 * @returns 链接集合
	 */
  public getTextLinkList(text: string, needDeWeight = true): string[] {
    const reg = new RegExp(URL_PATTERN, 'g');
    const list = text.match(reg) ?? [];
    if (needDeWeight) {
      return [...new Set(list)];
    }

    return list;
  }

  /**
	 * 添加链接解析的任务
	 * @param linkMsgContent 链接信息的内容
	 * @param linkList 信息内容中的链接集合
	 */
  public addLinkTask(linkMsgContent: string, linkList: string[]): void {
    const isLinkListEmpty = linkList.length <= 0;
    const isTaskExist = this.linkMsgResolveTaskMap.get(linkMsgContent);
    if (isLinkListEmpty || isTaskExist) {
      return;
    }

    this.linkMsgResolveTaskMap.set(linkMsgContent, linkList);
  }

  /**
	 * 链接任务解析
	 */
  public async resolveLinkTask(): Promise<boolean> {
    if (!this.linkMsgResolveTaskMap.size) {
      return false;
    }

    const taskMap = cloneDeep(this.linkMsgResolveTaskMap);
    this.linkMsgResolveTaskMap.clear();
    const pendingResolveLinkList: string[] = [];
    taskMap.forEach((linkList) => {
      pendingResolveLinkList.push(...linkList);
    });
    // 1.如果有缓存的，先返回缓存，hasFinish设置为false
    const { cacheLinkList, noCacheLinkList } = await this.getSortedLinkMap(pendingResolveLinkList);
    const hasResolveFinish = noCacheLinkList.length === 0;
    this.notifyLinkResolveUpdate(taskMap, cacheLinkList, hasResolveFinish);
    // 2.没有缓存的，就去拉取，并返回结果，设置hasFinish为true
    const resolveLinkList = await this.batchFetchResolveLinkMsg(noCacheLinkList);
    this.notifyLinkResolveUpdate(taskMap, resolveLinkList, true);
    const flag = cacheLinkList.length > 0 || resolveLinkList.length > 0;
    return flag;
  }

  /**
	 * 批量请求解析链接内容，并进行本地相关缓存变量处理
	 * - 适用于超过100条链接的请求解析（后台限制最多解析100条链接）
	 * @param linkList 待解析的链接列表
	 * @returns 成功解析的链接列表
	 */
  public async batchFetchResolveLinkMsg(linkList: string[]): Promise<string[]> {
    const threshold = 100;
    const totalResolveLinkList: string[] = [];
    for (let i = 0; i < linkList.length; i += threshold) {
      const pendingLinkList = linkList.slice(i, i + threshold);
      const resolveResolveLinkList = await this.fetchResolveLinkMsg(pendingLinkList);
      totalResolveLinkList.push(...resolveResolveLinkList);
    }

    return totalResolveLinkList;
  }

  /**
	 * 请求解析链接内容，并进行本地相关缓存变量处理
	 * - 适用于少于100条链接的请求解析（后台限制最多解析100条链接）
	 * @param linkList 待解析的链接列表
	 * @returns 成功解析的链接列表
	 */
  public async fetchResolveLinkMsg(linkList: string[]): Promise<string[]> {
    if (!linkList.length) {
      return [];
    }

    const resolveLinkList: string[] = [];
    const unResolveLinkList: string[] = [];
    const updateDBLinkMsgList: DB.ILinkMsgInfo[] = [];
    try {
      const { linkMsgInfos } = await LinkApi.getLinkMsgList(linkList);
      for (const link of linkList) {
        const item = linkMsgInfos[humps.camelize(link)];
        if (item) {
          const formatItem = {
            ...item,
            link,
            timestamp: Date.now(),
          };
          this.linkMsgMap.set(link, formatItem);
          updateDBLinkMsgList.push(formatItem);
          resolveLinkList.push(link);
        } else {
          unResolveLinkList.push(link);
        }
      }
    } catch (e) {
      logger.error('fetchLinkMsg error:', e);
    }
    if (updateDBLinkMsgList.length > 0) {
      const formatList = JSON.parse(JSON.stringify(updateDBLinkMsgList));
      DBManager.getInstance()?.bulkPutLinkMsgInfo(formatList);
    }
    unResolveLinkList.forEach((link) => {
      this.unResolveLinkSet.add(link);
    });
    return resolveLinkList;
  }

  /**
	 * 获取链接解析内容的列表
	 * @param linkList 链接列表
	 * @returns 链接解析内容的列表(按照linkList顺序排序，有就返回，没有就不返回)
	 */
  public async getLinkMsgList(linkList: string[]): Promise<DB.ILinkMsgInfo[]> {
    const linkMsgList: DB.ILinkMsgInfo[] = [];
    const linkMsgMap = await this.getLinkMsgMap(linkList);
    for (const link of linkList) {
      const item = linkMsgMap.get(link);
      if (item) {
        linkMsgList.push(item);
      }
    }

    return linkMsgList;
  }

  /**
	 * 链接解析内容是否有效(尚未过期)
	 * @param linkMsg 链接解析内容
	 * @returns 是否有效
	 */
  private isLinkMsgValid(linkMsg: DB.ILinkMsgInfo): boolean {
    const { timestamp } = linkMsg;
    const isCommonTimeValid = Date.now() - timestamp < this.maxCacheTime;
    return isCommonTimeValid;
  }

  /**
	 * 获取链接解析内容的Map(主要用于批量获取链接解析内容，判断链接是否已被解析)
	 * @param linkList 链接列表
	 * @returns 链接解析内容的Map
	 */
  private async getLinkMsgMap(linkList: string[]): Promise<Map<string, DB.ILinkMsgInfo>> {
    const linkMsgMap = new Map<string, DB.ILinkMsgInfo>();
    const noCacheLinkList: string[] = [];
    linkList.forEach((link) => {
      const cacheItem = this.linkMsgMap.get(link);
      if (cacheItem) {
        if (this.isLinkMsgValid(cacheItem)) {
          linkMsgMap.set(link, cacheItem);
        } else {
          linkMsgMap.delete(link);
          noCacheLinkList.push(link);
        }
      } else {
        noCacheLinkList.push(link);
      }
    });
    if (noCacheLinkList.length > 0) {
      const indexDBQueryList = await this.getLinkMsgListFromIndexDB(noCacheLinkList);
      indexDBQueryList.forEach((item) => {
        if (this.isLinkMsgValid(item)) {
          linkMsgMap.set(item.link, item);
        }
      });
    }

    return linkMsgMap;
  }

  /**
	 * 从IndexDB中获取链接解析内容的列表
	 * @param linkList 链接列表
	 * @returns 链接解析内容的列表
	 */
  private async getLinkMsgListFromIndexDB(linkList: string[]): Promise<DB.ILinkMsgInfo[]> {
    const unQueryLinkList = linkList.filter(link => !this.hasQueryLinkMsgFromIndexDBMap.get(link));
    const linkMsgList = await DBManager.getInstance()?.getLinkMsgList(unQueryLinkList);

    // 1.设置相关的取值标志位
    unQueryLinkList.forEach((link) => {
      this.hasQueryLinkMsgFromIndexDBMap.set(link, true);
    });
    // 2.同步记录到内存缓存中
    linkMsgList?.forEach((item) => {
      if (this.isLinkMsgValid(item)) {
        this.linkMsgMap.set(item.link, item);
      }
    });
    return linkMsgList ?? [];
  }

  /**
	 * 获取分好类的链接数组Map(识别哪些链接已有缓存，哪些链接需要请求分析)
	 * @param linkList 链接列表
	 * @returns 分好类的链接数组Map
	 */
  private async getSortedLinkMap(linkList: string[]): Promise<TSortedLinkMap> {
    const cacheLinkList: string[] = [];
    const noCacheLinkList: string[] = [];
    const linkMsgMap = await this.getLinkMsgMap(linkList);

    for (const link of linkList) {
      const item = linkMsgMap.get(link);
      if (item) {
        cacheLinkList.push(link);
      } else if (!this.unResolveLinkSet.has(link)) {
        noCacheLinkList.push(link);
      }
    }
    return {
      cacheLinkList,
      noCacheLinkList,
    };
  }

  /**
	 * 通知链接解析事件更新
	 * @param linkMsgResolveTaskMap 链接解析的任务Map
	 * @param resolveLinkList 解析成功的链接列表
	 * @param hasResolveFinish 解析是否完成
	 */
  private notifyLinkResolveUpdate(
    linkMsgResolveTaskMap: Map<string, string[]>,
    resolveLinkList: string[],
    hasResolveFinish: boolean,
  ): void {
    if (!resolveLinkList.length || !linkMsgResolveTaskMap.size) {
      return;
    }

    linkMsgResolveTaskMap.forEach((value, key) => {
      const linkList: string[] = [];
      for (const link of value) {
        if (resolveLinkList.includes(link)) {
          linkList.push(link);
        }
      }
      if (linkList.length > 0) {
        this.linkEventBus.emit(LinkEventType.resolve, {
          linkList,
          msgContent: key,
          hasResolveFinish,
        });
      }
    });
  }
}

export { LinkManager };
